import { generateViewId } from "@domain/Settings";
import type { Job } from "../types";
import { FILTER_FIELD_BY_ID } from "./jobFilterFields";
import { OPERATORS_BY_TYPE, defaultOperator, matchOperator, type OperatorId } from "./jobFilterOperators";

export type Conjunction = "and" | "or";
export type FilterValue = string | number | boolean | string[] | null;

export interface FilterCondition {
	kind: "condition";
	id: string;
	fieldId: string;
	operator: OperatorId;
	value: FilterValue;
}

export interface FilterGroup {
	kind: "group";
	id: string;
	conjunction: Conjunction;
	conditions: FilterCondition[];
}

export type FilterNode = FilterCondition | FilterGroup;

export interface JobFilterTree {
	conjunction: Conjunction;
	children: FilterNode[];
}

export const EMPTY_FILTER_TREE: JobFilterTree = { conjunction: "and", children: [] };

/** The field a fresh condition targets by default (first in the registry). */
const DEFAULT_FIELD_ID = "field";

/** Build a fresh, inert condition (blank value → skipped until the user fills it). */
export function createCondition(fieldId: string = DEFAULT_FIELD_ID): FilterCondition {
	const field = FILTER_FIELD_BY_ID.get(fieldId) ?? FILTER_FIELD_BY_ID.get(DEFAULT_FIELD_ID)!;
	return {
		kind: "condition",
		id: generateViewId(),
		fieldId: field.id,
		operator: defaultOperator(field.type),
		value: field.type === "multiSelect" ? [] : null,
	};
}

/** Build a fresh group seeded with one condition (an empty group is pointless). */
export function createGroup(): FilterGroup {
	return { kind: "group", id: generateViewId(), conjunction: "and", conditions: [createCondition()] };
}

/** Operators that stand on their own (no operand). */
const VALUELESS_OPS = new Set<OperatorId>(["isEmpty", "isNotEmpty"]);

/** A condition is effective when it can constrain results: a known field, and
 *  either a valueless operator or a non-blank value. Ineffective ones are skipped. */
function isEffective(c: FilterCondition): boolean {
	if (!FILTER_FIELD_BY_ID.has(c.fieldId)) return false;
	if (VALUELESS_OPS.has(c.operator)) return true;
	const v = c.value;
	if (v === null || v === undefined) return false;
	if (typeof v === "string") return v.trim() !== "";
	if (Array.isArray(v)) return v.length > 0;
	return true; // number / boolean
}

function evalCondition(job: Job, c: FilterCondition): boolean {
	const field = FILTER_FIELD_BY_ID.get(c.fieldId);
	if (!field) return true; // unknown field → no constraint
	return matchOperator(field.type, c.operator, field.getValue(job), c.value);
}

function evalGroup(job: Job, group: FilterGroup): boolean | null {
	const effective = group.conditions.filter(isEffective);
	if (effective.length === 0) return null; // no constraint
	return group.conjunction === "and"
		? effective.every((c) => evalCondition(job, c))
		: effective.some((c) => evalCondition(job, c));
}

/** Evaluate one root child; returns null when it imposes no constraint. */
function evalNode(job: Job, node: FilterNode): boolean | null {
	if (node.kind === "group") return evalGroup(job, node);
	return isEffective(node) ? evalCondition(job, node) : null;
}

/** Filter jobs by the tree. Returns the SAME array reference when the tree has
 *  no effective constraints (parity with the old `applyJobFilters` fast path). */
export function applyJobFilterTree(jobs: readonly Job[], tree: JobFilterTree): Job[] {
	if (activeConditionCount(tree) === 0) return jobs as Job[];
	return (jobs as Job[]).filter((job) => {
		const results = tree.children.map((n) => evalNode(job, n)).filter((r): r is boolean => r !== null);
		if (results.length === 0) return true;
		return tree.conjunction === "and" ? results.every(Boolean) : results.some(Boolean);
	});
}

/** Count effective conditions across root and groups — drives the filter badge. */
export function activeConditionCount(tree: JobFilterTree): number {
	let n = 0;
	for (const node of tree.children) {
		if (node.kind === "group") n += node.conditions.filter(isEffective).length;
		else if (isEffective(node)) n += 1;
	}
	return n;
}

/** Structural equality via canonical JSON. Trees are built by the same helpers
 *  with stable key order (and a draft starts as a copy of the applied tree), so
 *  this is a reliable dirty check for the Apply gate. */
export function filterTreesEqual(a: JobFilterTree, b: JobFilterTree): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

// ── Immutable mutation helpers (used by the FilterBuilder UI) ──────────────────

/** Map every condition in the tree (root-level + inside groups) through `fn`. */
function mapConditions(tree: JobFilterTree, fn: (c: FilterCondition) => FilterCondition): JobFilterTree {
	return {
		...tree,
		children: tree.children.map((node) =>
			node.kind === "group" ? { ...node, conditions: node.conditions.map(fn) } : fn(node),
		),
	};
}

export function addCondition(tree: JobFilterTree, groupId?: string): JobFilterTree {
	if (!groupId) return { ...tree, children: [...tree.children, createCondition()] };
	return {
		...tree,
		children: tree.children.map((node) =>
			node.kind === "group" && node.id === groupId
				? { ...node, conditions: [...node.conditions, createCondition()] }
				: node,
		),
	};
}

export function addGroup(tree: JobFilterTree): JobFilterTree {
	return { ...tree, children: [...tree.children, createGroup()] };
}

export function removeNode(tree: JobFilterTree, nodeId: string): JobFilterTree {
	return {
		...tree,
		children: tree.children
			.filter((node) => node.id !== nodeId)
			.map((node) =>
				node.kind === "group" ? { ...node, conditions: node.conditions.filter((c) => c.id !== nodeId) } : node,
			),
	};
}

export function setConditionField(tree: JobFilterTree, conditionId: string, fieldId: string): JobFilterTree {
	return mapConditions(tree, (c) => {
		if (c.id !== conditionId) return c;
		const field = FILTER_FIELD_BY_ID.get(fieldId);
		if (!field) return c;
		return { ...c, fieldId, operator: defaultOperator(field.type), value: field.type === "multiSelect" ? [] : null };
	});
}

export function setConditionOperator(tree: JobFilterTree, conditionId: string, operator: OperatorId): JobFilterTree {
	return mapConditions(tree, (c) => (c.id === conditionId ? { ...c, operator } : c));
}

export function setConditionValue(tree: JobFilterTree, conditionId: string, value: FilterValue): JobFilterTree {
	return mapConditions(tree, (c) => (c.id === conditionId ? { ...c, value } : c));
}

export function setConjunction(tree: JobFilterTree, conjunction: Conjunction, groupId?: string): JobFilterTree {
	if (!groupId) return { ...tree, conjunction };
	return {
		...tree,
		children: tree.children.map((node) =>
			node.kind === "group" && node.id === groupId ? { ...node, conjunction } : node,
		),
	};
}

// ── Deserialization guard (localStorage → tree) ───────────────────────────────

function sanitizeConjunction(v: unknown): Conjunction {
	return v === "or" ? "or" : "and";
}

function sanitizeCondition(raw: unknown): FilterCondition | null {
	if (typeof raw !== "object" || raw === null) return null;
	const r = raw as Record<string, unknown>;
	if (typeof r.fieldId !== "string" || typeof r.operator !== "string") return null;
	const field = FILTER_FIELD_BY_ID.get(r.fieldId);
	if (!field) return null;
	const validOps = OPERATORS_BY_TYPE[field.type].map((o) => o.id);
	if (!validOps.includes(r.operator as OperatorId)) return null;
	const value = (r.value ?? null) as FilterValue;
	return {
		kind: "condition",
		id: typeof r.id === "string" ? r.id : generateViewId(),
		fieldId: r.fieldId,
		operator: r.operator as OperatorId,
		value,
	};
}

/** Coerce untrusted JSON (from localStorage) into a valid tree. Unknown fields
 *  or type-mismatched operators are dropped; malformed input yields the empty
 *  tree. Never throws — a schema drift must not crash the page or wipe silently. */
export function sanitizeFilterTree(raw: unknown): JobFilterTree {
	if (typeof raw !== "object" || raw === null) return EMPTY_FILTER_TREE;
	const r = raw as Record<string, unknown>;
	if (!Array.isArray(r.children)) return EMPTY_FILTER_TREE;
	const children: FilterNode[] = [];
	for (const child of r.children) {
		if (typeof child !== "object" || child === null) continue;
		const c = child as Record<string, unknown>;
		if (c.kind === "group") {
			const conditions = Array.isArray(c.conditions)
				? c.conditions.map(sanitizeCondition).filter((x): x is FilterCondition => x !== null)
				: [];
			children.push({
				kind: "group",
				id: typeof c.id === "string" ? c.id : generateViewId(),
				conjunction: sanitizeConjunction(c.conjunction),
				conditions,
			});
		} else {
			const cond = sanitizeCondition(child);
			if (cond) children.push(cond);
		}
	}
	return { conjunction: sanitizeConjunction(r.conjunction), children };
}
